import WindowedSelect, { components } from "react-windowed-select";
import { useCallback } from "react";
import SectionCard from "../../Components/SectionCard";
import TextboxSet from "../../Components/TextboxSet";
import { XMarkIcon } from "@heroicons/react/24/solid";
import ImageUpload from "../../Components/ImageUpload";
import { loadGoogleFont } from "../../Constant/fontLoader";

const FontWrapper = (Component, fontOptions) => (props) => {
  const font = fontOptions.find(
    (f) => String(f.id) === String(props.data?.value)
  );
  const family = font ? font.family : "inherit";

  return (
    <Component {...props}>
      {props.data ? (
        <span
          style={{
            fontFamily: family === "inherit" ? family : `'${family}'`,
            fontWeight: 400,
          }}
        >
          {props.data.label}
        </span>
      ) : (
        props.children
      )}
    </Component>
  );
};

const Customizations = ({
  templateCustomizations,
  setTemplateCustomizations,
  fontOptions,
}) => {
  const selectedTitleFont = fontOptions.find(
    (f) => String(f.id) === String(templateCustomizations.title_font)
  );
  const selectedTitleFontFamily = selectedTitleFont
    ? selectedTitleFont.family
    : "inherit";

  const selectedContentFont = fontOptions.find(
    (f) => String(f.id) === String(templateCustomizations.content_font)
  );
  const selectedContentFontFamily = selectedContentFont
    ? selectedContentFont.family
    : "inherit";

  const handleChangeFont = useCallback(
    (selected, actionMeta) => {
      const { name } = actionMeta;
      const value = selected ? selected.value : null;

      setTemplateCustomizations({ ...templateCustomizations, [name]: value });

      const selectedFont = fontOptions.find(
        (font) => String(font.id) === String(value)
      );
      if (selectedFont) loadGoogleFont(selectedFont.family);
    },
    [templateCustomizations, setTemplateCustomizations, fontOptions]
  );

  const handleImageChange = (value) => {
    setTemplateCustomizations({ ...templateCustomizations, image: value });
  };

  const handleRemoveImage = () => {
    setTemplateCustomizations({ ...templateCustomizations, image: "" });
  };

  return (
    <div>
      <SectionCard title="Customizations">
        <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-3 md:gap-6 p-[24px]">
          <div className="flex flex-col gap-3 md:gap-6">
            <div className="flex flex-col w-full h-full gap-2">
              <label>Title Font Example</label>
              <div className="w-full h-full border border-[#EFF0F2] rounded-lg p-6 justify-center items-center flex">
                <h1
                  className="text-[40px] text-center"
                  style={{
                    fontFamily: selectedTitleFontFamily,
                  }}
                >
                  This is an example
                </h1>
              </div>
            </div>
            <div className="w-full">
              <label className="block text-sm font-medium mb-1">
                Title Font
              </label>
              <WindowedSelect
                name="title_font"
                value={
                  fontOptions.find(
                    (f) =>
                      String(f.id) === String(templateCustomizations.title_font)
                  ) || null
                }
                options={fontOptions}
                isDisabled={fontOptions.length === 0}
                components={{
                  Option: FontWrapper(components.Option, fontOptions),
                  SingleValue: FontWrapper(components.SingleValue, fontOptions),
                }}
                onChange={handleChangeFont}
                onMenuOpen={() => {
                  fontOptions.forEach((font) => loadGoogleFont(font.family));
                }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col w-full h-full gap-2">
              <label>Content Font Example</label>
              <div className="w-full h-full border border-[#EFF0F2] rounded-lg p-6 justify-center items-center flex">
                <p
                  className="text-center"
                  style={{
                    fontFamily: selectedContentFontFamily,
                  }}
                >
                  This is an example
                </p>
              </div>
            </div>
            <div className="w-full">
              <label className="block text-sm font-medium mb-1">
                Content Font
              </label>
              <WindowedSelect
                name="content_font"
                value={
                  fontOptions.find(
                    (f) =>
                      String(f.id) ===
                      String(templateCustomizations.content_font)
                  ) || null
                }
                options={fontOptions}
                isDisabled={fontOptions.length === 0}
                components={{
                  Option: FontWrapper(components.Option, fontOptions),
                  SingleValue: FontWrapper(components.SingleValue, fontOptions),
                }}
                onChange={handleChangeFont}
                onMenuOpen={() => {
                  fontOptions.forEach((font) => loadGoogleFont(font.family));
                }}
              />
            </div>
          </div>

          <TextboxSet
            name="theme_color"
            label="Theme Color"
            type="color"
            className="p-0 h-[40px] w-[80px]"
            onChange={(e) =>
              setTemplateCustomizations({
                ...templateCustomizations,
                theme_color: e.target.value,
              })
            }
            value={templateCustomizations.theme_color}
          />

          <div className="grid md:col-span-2 w-full">
            <label className="block text-sm font-medium mb-1">
              Background Image
            </label>
            <ImageUpload handleChange={handleImageChange} />
            {templateCustomizations.image && (
              <div className="relative flex flex-col w-full max-h-[200px] bg-black rounded-xl justify-center items-center">
                <div className="absolute left-[20px] top-[8px] flex items-center gap-[18px]">
                  <XMarkIcon
                    className="size-[16px] h-fit text-white hover:cursor-pointer"
                    onClick={handleRemoveImage}
                  />
                  <p className="h-fit text-white font-normal">
                    {decodeURIComponent(
                      templateCustomizations.image
                        .split("?")[0]
                        .split("/")
                        .pop()
                    )}
                  </p>
                </div>
                <img
                  src={templateCustomizations.image}
                  alt="event-image"
                  className="h-[200px] object-contain"
                />
              </div>
            )}
          </div>
        </div>
      </SectionCard>
    </div>
  );
};

export default Customizations;
